import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CONST_URL_SEGEMENTS } from '@fitness-force/constants';
import { AppErrors, NotFoundError } from '@fitness-force/models';;
import type { AppointmentTag, CreateAppointmentTypesRequest } from '@fitness-force/models';
import { environment } from '@fitness-force/environments';
import { throwError, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import type { AppointmentTypeList } from '@fitness-force/models';

@Injectable({
  providedIn: 'root',
})
export class AppointmentTypesService {
  private http = inject(HttpClient);
  private router = inject(Router);

  BASE_URL: string = environment.BASE_URL;
  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  createAppointmentTypes(
    tenantId: number,
    createAppointmentTypesRequest: CreateAppointmentTypesRequest
  ) {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.SCHEDULING.APPOINTMENT_TYPE.CREATE_APPOINTMENT_TYPE
      .replace('{tenant_id}', tenantId.toString());
    return this.http
      .post(
        url,
        createAppointmentTypesRequest,
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  getAppointmentTypes(
    tenantId: number,
    allParams: Array<string>
  ): Observable<AppointmentTypeList> {
    let parsedParams = new HttpParams();
    if (allParams.length > 0) {
      allParams.forEach((p: any) => {
        parsedParams = parsedParams.append(p.name, p.value);
      });
    }
    const url =
      this.BASE_URL +
      CONST_URL_SEGEMENTS.SCHEDULING.APPOINTMENT_TYPE.GET_APPOINTMENT_TYPES.replace(
        '{tenant_id}',
        tenantId.toString()
      );
    return this.http
      .get(url, { headers: this.httpOptions.headers, params: parsedParams })
      .pipe(
        map((res: any) => {
          if (res) {
            return res;
          }
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }
  getSingleAppointmentType(tenantId: number, appt_type_id: number) {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.SCHEDULING.APPOINTMENT_TYPE.GET_APPOINTMENT_TYPE_BY_ID
      .replace('{tenant_id}', tenantId.toString())
      .replace('{appt_type_id}', appt_type_id.toString());
    return this.http
      .get(
        url,
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  updateAppointmentTypes(tenantId: number, appt_type_id: number, data: any) {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.SCHEDULING.APPOINTMENT_TYPE.UPDATE_APPOINTMENT_TYPE
      .replace('{tenant_id}', tenantId.toString())
      .replace('{appt_type_id}', appt_type_id.toString());
    return this.http
      .put(
        url,
        data,
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  markActiveAppointmentTypes(tenantId: number, appt_type_id: number) {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.SCHEDULING.APPOINTMENT_TYPE.MARK_APPOINTMENT_TYPE_ACTIVE
      .replace('{tenant_id}', tenantId.toString())
      .replace('{appt_type_id}', appt_type_id.toString());
    return this.http
      .post(
        url,
        {},
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  markInActiveAppointmentTypes(tenantId: number, appt_type_id: number) {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.SCHEDULING.APPOINTMENT_TYPE.MARK_APPOINTMENT_TYPE_INACTIVE
      .replace('{tenant_id}', tenantId.toString())
      .replace('{appt_type_id}', appt_type_id.toString());
    return this.http
      .post(
        url,
        {},
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }
  // TAGS
  getAppointmentTags(tenantId: number): Observable<AppointmentTag[]> {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.SCHEDULING.APPOINTMENT_TYPE.GET_APPOINTMENT_TAGS
      .replace('{tenant_id}', tenantId.toString());
    return this.http
      .get(
        url,
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res.items;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  getAppointmentTag(tenantId: number,tagId: number): Observable<AppointmentTag[]> {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.SCHEDULING.APPOINTMENT_TYPE.GET_APPOINTMENT_TAG_BY_ID
      .replace('{tenant_id}', tenantId.toString())
      .replace('{tag_id}', tagId.toString());
    return this.http
      .get(
        url,
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res.items;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  createAppointmentTag(tenantId: number, data: any) {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.SCHEDULING.APPOINTMENT_TYPE.CREATE_APPOINTMENT_TAG
      .replace('{tenant_id}', tenantId.toString());
    return this.http
      .post(
        url,
        data,
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  updateAppointmentTag(tenantId: number,appt_tag_id:number, data: any) {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.SCHEDULING.APPOINTMENT_TYPE.UPDATE_APPOINTMENT_TAG
      .replace('{tenant_id}', tenantId.toString())
      .replace('{tag_id}', appt_tag_id.toString());
    return this.http
      .put(
        url,
        data,
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  // /tenants/{tenant_id}/appointment_types  .. Its in review merge ...
  // /tenants/{tenant_id}/class_appt_tags
  getAppointmentSubTypes(tenantId: number, appt_type_id: number) {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.SCHEDULING.APPOINTMENT_SUB_TYPE.GET_APPOINTMENT_SUB_TYPES
      .replace('{tenant_id}', tenantId.toString())
      .replace('{appt_type_id}', appt_type_id.toString());
    return this.http
      .get(
        url,
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }
}
